import React, { useEffect, useRef, useState } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Slider } from '../ui/slider';
import { Bell, Play, Check, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { SOUND_OPTIONS, previewSound, setSoundVolume } from '../workouts/restTimerSounds';

/**
 * Iter. 411.mm8 + mm11 — Rest-timer sound picker + volume slider.
 * 4 packs (all synthesized via Web Audio API, zero bundle cost): Classic beep,
 * Air horn (klaxon), Gong, Boxing bell. Preview button plays the end pattern
 * so the user hears the choice before saving. The volume slider (0-100%)
 * lets users adapt to environment (gym boombox vs quiet apartment).
 */
const SettingsRestTimerSound = () => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const [saving, setSaving] = useState(false);
  const current = user?.rest_timer_sound || 'beep';

  // Hydrate volume from profile (0..100). Default 80 to keep legacy behaviour
  // (previous code hardcoded scaled volumes at ~0.7-0.8 peak).
  const initialVolume = typeof user?.rest_timer_volume === 'number'
    ? Math.max(0, Math.min(100, user.rest_timer_volume))
    : 80;
  const [volume, setVolume] = useState(initialVolume);
  const saveDebounceRef = useRef(null);

  // Apply the current volume to the shared module on mount + any change
  // so previews use the exact value the user is dragging.
  useEffect(() => { setSoundVolume(volume / 100); }, [volume]);

  const L = (fr, en, es) => language === 'es' ? es : (language === 'en' ? en : fr);
  const labelFor = (opt) => language === 'es' ? opt.label_es : (language === 'fr' ? opt.label_fr : opt.label_en);

  const handleSelect = async (id) => {
    if (id === current || saving) return;
    setSaving(true);
    try {
      const res = await api.put('/users/profile', { rest_timer_sound: id });
      updateUser(res.data);
      previewSound(id); // instant audio feedback of the selection
      toast.success(L('Son de repos activé', 'Rest sound activated', 'Sonido de descanso activado'));
    } catch {
      toast.error(L('Erreur de sauvegarde', 'Save error', 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (e, id) => {
    e.stopPropagation();
    previewSound(id);
  };

  // Debounced save on volume change (avoid hammering the API on every drag tick)
  const handleVolumeChange = (values) => {
    const v = Array.isArray(values) ? values[0] : values;
    setVolume(v);
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(async () => {
      try {
        const res = await api.put('/users/profile', { rest_timer_volume: v });
        updateUser(res.data);
      } catch {
        // Non-blocking — local slider still works, next slider drag retries
      }
    }, 500);
  };

  // Play a short preview of the current sound when the user releases the slider
  // so they immediately hear the new volume level.
  const handleVolumeCommit = () => {
    previewSound(current);
  };

  return (
    <Card
      className="border-amber-400/30 transition-all"
      style={{
        background: 'linear-gradient(160deg, rgba(251,191,36,0.10) 0%, rgba(0,0,0,0.65) 100%)',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 0 18px rgba(251,191,36,0.22), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
      data-testid="settings-rest-timer-sound-card"
    >
      <CardHeader>
        <CardTitle
          className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-amber-100"
          style={{ textShadow: '0 0 8px rgba(251,191,36,0.5)' }}
        >
          <Bell className="w-5 h-5 text-amber-300" style={{ filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.7))' }} />
          {L('Sonnerie fin de repos', 'End-of-rest sound', 'Sonido fin de descanso')}
        </CardTitle>
        <CardDescription className="text-amber-100/70">
          {L(
            'Choisis l\'alerte qui sonnera à la fin de chaque pause entre exercices.',
            'Pick the alert that plays at the end of each rest between exercises.',
            'Elige la alerta que sonará al final de cada descanso entre ejercicios.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {SOUND_OPTIONS.map((opt) => {
            const selected = opt.id === current;
            return (
              <button
                key={opt.id}
                onClick={() => handleSelect(opt.id)}
                disabled={saving}
                data-testid={`rest-sound-${opt.id}-btn`}
                className={`relative p-3 rounded-xl border transition-all text-left ${
                  selected
                    ? 'border-amber-400/70 bg-amber-400/10'
                    : 'border-border/50 bg-black/30 hover:border-amber-400/40'
                }`}
                style={selected ? { boxShadow: '0 0 14px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.06)' } : undefined}
              >
                {selected && (
                  <div
                    className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center bg-amber-400"
                    style={{ boxShadow: '0 0 8px rgba(251,191,36,0.7)' }}
                  >
                    <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl" aria-hidden="true">{opt.emoji}</span>
                  <p className={`font-semibold text-sm ${selected ? 'text-amber-100' : 'text-foreground'}`}>
                    {labelFor(opt)}
                  </p>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handlePreview(e, opt.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePreview(e, opt.id); }}
                  data-testid={`rest-sound-${opt.id}-preview`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300/80 hover:text-amber-200 cursor-pointer"
                >
                  <Play className="w-3 h-3" />
                  {L('Écouter', 'Preview', 'Escuchar')}
                </div>
              </button>
            );
          })}
        </div>

        {/* Volume slider — 0-100% for gym vs quiet room control */}
        <div className="mt-5 pt-4 border-t border-amber-400/20" data-testid="rest-sound-volume-block">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-amber-100">
              {volume === 0 ? (
                <VolumeX className="w-4 h-4 text-amber-300/60" />
              ) : (
                <Volume2 className="w-4 h-4 text-amber-300" style={{ filter: 'drop-shadow(0 0 3px rgba(251,191,36,0.6))' }} />
              )}
              <span className="text-sm font-semibold">
                {L('Volume', 'Volume', 'Volumen')}
              </span>
            </div>
            <span
              className="text-sm font-mono text-amber-200 tabular-nums"
              data-testid="rest-sound-volume-value"
            >
              {volume === 0 ? L('Muet', 'Muted', 'Silencio') : `${volume}%`}
            </span>
          </div>
          <Slider
            value={[volume]}
            min={0}
            max={100}
            step={5}
            onValueChange={handleVolumeChange}
            onValueCommit={handleVolumeCommit}
            className="[&_[role=slider]]:bg-amber-400 [&_[role=slider]]:border-amber-500 [&>span:first-child]:bg-amber-500/20 [&>span:first-child>span]:bg-gradient-to-r [&>span:first-child>span]:from-amber-400 [&>span:first-child>span]:to-orange-400"
            data-testid="rest-sound-volume-slider"
            aria-label={L('Volume sonnerie', 'Ring volume', 'Volumen de sonido')}
          />
          <p className="mt-2 text-[11px] text-amber-100/60 leading-snug">
            {L(
              'Baisse pour un appart calme, monte pour la salle bruyante. Relâche le curseur pour un aperçu.',
              'Lower for a quiet flat, crank it up for a loud gym. Release the slider for a preview.',
              'Baja para un piso tranquilo, sube para el gimnasio ruidoso. Suelta el deslizador para escuchar.'
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SettingsRestTimerSound;
